import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { visitsApi, leadsApi, propertiesApi } from '../api/endpoints';
import { useAuth } from '../context/AuthContext';
import { DeleteButton } from '../components/DeleteButton';
import { toast } from 'sonner';
import { useTeamOptions } from '../hooks/useTeamOptions';

/**
 * FIXED ID GENERATOR
 */
const formatVisitId = (rawId, dateStr) => {
  if (!rawId) return 'SV-NEW';
  const year = dateStr ? new Date(dateStr).getFullYear() || new Date().getFullYear() : new Date().getFullYear();
  const numericOnly = rawId.toString().replace(/SV/gi, '').replace(/\D/g, '');
  const sequence = numericOnly.length > 4 ? numericOnly.slice(-4) : numericOnly.padStart(3, '0');
  return `SV-${year}-${sequence}`;
};

/**
 * FUZZY DATE MATCHER (Fixes API Dummy Data Issues)
 */
const isDateToday = (apiDateString) => {
  if (!apiDateString) return false;
  
  const str = String(apiDateString).trim().toLowerCase();
  
  // 1. Match literal text like "today" if your dummy API returns that instead of a date format
  if (str === 'today') return true;

  const today = new Date();
  
  // 2. Strict standard HTML5 Date Match (YYYY-MM-DD)
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  const html5Date = `${yyyy}-${mm}-${dd}`;
  
  if (str.includes(html5Date)) return true;

  // 3. Native Javascript Date match fallback
  try {
    const parsed = new Date(apiDateString);
    if (!isNaN(parsed.getTime())) {
      return parsed.toDateString() === today.toDateString();
    }
  } catch (e) {
    // Ignore invalid formats
  }

  return false;
};

const statusColor = {
  Scheduled: 'bg-blue-50 text-blue-700 border-blue-200',
  Completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Rescheduled: 'bg-amber-50 text-amber-700 border-amber-200',
  'No-show': 'bg-rose-50 text-rose-700 border-rose-200',
};

const VisitFormDialog = ({ leads, properties, onSaved, trigger, initial }) => {
  const isEdit = !!initial;
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    lead: '', property: '', date: '', time: '10:00 AM',
    status: 'Scheduled', agent: '', feedback: '',
  });
  const team = useTeamOptions();

  useEffect(() => {
    if (open) {
      setForm({
        lead: initial?.lead || (leads[0]?.name || ''),
        property: initial?.property || (properties[0]?.name || ''),
        date: initial?.date || '',
        time: initial?.time || '10:00 AM',
        status: initial?.status || 'Scheduled',
        agent: initial?.agent || '',
        feedback: initial?.feedback || '',
      });
    }
  }, [open, initial, leads, properties]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.lead || !form.property || !form.date) { 
      toast.error('Lead, project and date are required'); 
      return; 
    }
    setSaving(true);
    try {
      const saved = isEdit
        ? await visitsApi.update(initial.id, form)
        : await visitsApi.create(form);
      onSaved(saved, isEdit);
      toast.success(isEdit ? 'Visit updated' : 'Site visit scheduled');
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save visit');
    } finally { setSaving(false); }
  };

  const uniqueLeads = [...new Map(leads.map(l => [l.id, l])).values()];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {isEdit ? `Edit Visit ${formatVisitId(initial.id, initial.date)}` : 'Schedule Site Visit'}
          </DialogTitle>
          <DialogDescription>{isEdit ? 'Update visit details and feedback.' : 'Plan a project site visit with a lead.'}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label>Lead *</Label>
            <Select value={form.lead} onValueChange={v => update('lead', v)}>
              <SelectTrigger><SelectValue placeholder="Select a lead" /></SelectTrigger>
              <SelectContent>{uniqueLeads.map(l => <SelectItem key={l.id} value={l.name}>{l.name} — {l.phone}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label>Project *</Label>
            <Select value={form.property} onValueChange={v => update('property', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Visit Date *</Label><Input type="date" value={form.date} onChange={e => update('date', e.target.value)} /></div>
          <div>
            <Label>Time</Label>
            <Select value={form.time} onValueChange={v => update('time', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{['09:00 AM','10:00 AM','11:30 AM','01:00 PM','03:30 PM','05:00 PM','06:30 PM'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={v => update('status', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{['Scheduled','Completed','Rescheduled','No-show'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Assign Agent</Label>
            <Select value={form.agent} onValueChange={v => update('agent', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{team.map(u => <SelectItem key={u.id} value={u.name}>{u.name} — {u.role}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2"><Label>Notes / Feedback</Label><Input value={form.feedback} onChange={e => update('feedback', e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className="bg-brand-navy hover:bg-[#13294A] text-white" onClick={submit} disabled={saving}>{saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Schedule Visit')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SiteVisits = () => {
  const { hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const canCreate = hasPermission('site_visits', 'create');
  const canEdit = hasPermission('site_visits', 'edit');
  const canDelete = hasPermission('site_visits', 'delete');

  const [data, setData] = useState([]);
  const [leads, setLeads] = useState([]);
  const [props, setProps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  
  const [applyTodayFilter, setApplyTodayFilter] = useState(false);
  const [applyScheduledFilter, setApplyScheduledFilter] = useState(false);
  const [usedFallback, setUsedFallback] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const [v, l, p] = await Promise.all([
          visitsApi.list(),
          leadsApi.list().catch(() => []),
          propertiesApi.list().catch(() => []),
        ]);
        setData(v); setLeads(l); setProps(p);
      } catch (e) {
        console.error('Failed to load site visits', e);
        toast.error('Failed to load site visits');
      } finally { setLoading(false); }
    })();
  }, []);

  // Sync state parameters from Dashboard summary card navigation values
  useEffect(() => {
    setApplyTodayFilter(location.state?.filterDate === 'today');
    setApplyScheduledFilter(location.state?.filterStatus === 'scheduled_only');
    setUsedFallback(false);
  }, [location.state]);

  // Execute Filter Computation
  const filteredData = useMemo(() => {
    let result = data;
    
    // 1. FILTER BY STATUS
    if (applyScheduledFilter) {
      result = result.filter(visit => {
        const status = String(visit.status || '').toLowerCase().trim();
        return status === 'scheduled' || status === 'rescheduled';
      });
    }

    // 2. FILTER BY DATE
    if (applyTodayFilter) {
      const exactMatches = result.filter(visit => isDateToday(visit.date));
      if (exactMatches.length === 0 && result.length > 0) {
        setUsedFallback(true);
      } else {
        setUsedFallback(false);
        result = exactMatches;
      }
    }

    // 3. SEARCH
    if (searchQ) {
      const ql = searchQ.toLowerCase();
      result = result.filter(v =>
        (v.lead || '').toLowerCase().includes(ql) ||
        (v.property || '').toLowerCase().includes(ql) ||
        (v.agent || '').toLowerCase().includes(ql) ||
        (v.id || '').toLowerCase().includes(ql)
      );
    }

    // 4. PRIORITY
    if (priorityFilter !== 'all') {
      result = result.filter(v => (v.priority || 'Interested') === priorityFilter);
    }

    return result;
  }, [data, applyTodayFilter, applyScheduledFilter, searchQ, priorityFilter]);

  const todayUIString = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  const clearFilters = () => {
    setApplyTodayFilter(false);
    setApplyScheduledFilter(false);
    setUsedFallback(false);
    navigate('.', { replace: true, state: null });
  };

  const onSaved = (saved, isEdit) => {
    if (isEdit) setData(d => d.map(x => x.id === saved.id ? saved : x));
    else setData(d => [saved, ...d]);
  };

  const onDelete = async (v) => {
    await visitsApi.remove(v.id);
    setData(d => d.filter(x => x.id !== v.id));
    toast.success('Visit removed');
  };

  return (
    <div className="space-y-5">
      {/* Active Filter Notification Banner */}
      {(applyTodayFilter || applyScheduledFilter) && (
        <div className={`p-3 border rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs font-medium animate-in fade-in zoom-in duration-300 ${usedFallback ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
          <div className="flex items-center gap-2">
            <Icons.Filter className={`w-4 h-4 shrink-0 ${usedFallback ? 'text-amber-600' : 'text-blue-600'}`} />
            <span>
              {usedFallback ? (
                <>No visits exactly matched today's calendar date in dummy data. Falling back to show <strong>All {applyScheduledFilter ? 'Scheduled & Rescheduled' : 'Pending'}</strong> visits.</>
              ) : (
                <>Showing <strong>{applyScheduledFilter ? 'Scheduled & Rescheduled' : 'All'}</strong> site visits for <strong>{applyTodayFilter ? `today (${todayUIString})` : 'all dates'}</strong>.</>
              )}
            </span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={clearFilters} 
            className={`h-7 font-bold uppercase tracking-wider text-[10px] shrink-0 bg-white shadow-sm ${usedFallback ? 'text-amber-700 hover:bg-amber-100 hover:text-amber-900' : 'text-blue-700 hover:bg-blue-100 hover:text-blue-900'}`}
          >
            Clear Filters
          </Button>
        </div>
      )}

      <Card className="border-slate-200">
        <CardContent className="p-3 sm:p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-1 flex-wrap min-w-0">
            <Tabs value={view} onValueChange={setView}>
              <TabsList>
                <TabsTrigger value="list"><Icons.List className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">List</span></TabsTrigger>
                <TabsTrigger value="timeline"><Icons.Clock className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Timeline</span></TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative flex-1 min-w-[160px] max-w-sm">
              <Icons.Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="visits-search" className="pl-9 h-9" placeholder="Search lead / project / agent..." value={searchQ} onChange={e => setSearchQ(e.target.value)} />
            </div>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="h-9 w-[130px]"><SelectValue placeholder="Priority" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                <SelectItem value="Hot">Hot</SelectItem>
                <SelectItem value="Interested">Interested</SelectItem>
                <SelectItem value="Average">Average</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {canCreate && (
            <VisitFormDialog
              leads={leads} properties={props}
              onSaved={onSaved}
              trigger={
                <Button className="bg-brand-navy hover:bg-[#13294A] text-white" data-testid="new-visit-btn">
                  <Icons.Plus className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Schedule Visit</span>
                </Button>
              }
            />
          )}
        </CardContent>
      </Card>

      {loading && <Skeleton className="h-72" />}
      
      {!loading && view === 'list' ? (
        <Card className="border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3 hidden sm:table-cell">ID No.</th>
                  <th className="px-4 py-3">Lead</th>
                  <th className="px-4 py-3 hidden md:table-cell">Project</th>
                  <th className="px-4 py-3">Date/Time</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Agent</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredData.length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-8 text-center">
                      <div className="flex flex-col items-center justify-center text-slate-400">
                        <Icons.CalendarX className="w-10 h-10 mb-2 opacity-50" />
                        <span className="italic font-medium">No matching site visits found for these criteria.</span>
                        {(applyTodayFilter || applyScheduledFilter) && (
                          <Button variant="link" onClick={clearFilters} className="mt-2 text-brand-navy">
                            Show all visits instead
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                {filteredData.map(v => (
                  <tr key={v.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-[11px] text-brand-gold font-bold hidden sm:table-cell">
                      {formatVisitId(v.id, v.date)}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-800">{v.lead}</td>
                    <td className="px-4 py-3 text-slate-700 truncate max-w-[200px] hidden md:table-cell">{v.property}</td>
                    <td className="px-4 py-3">
                      <div className="text-slate-800 font-medium">{v.date}</div>
                      <div className="text-[11px] text-slate-500">{v.time}</div>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <div className="flex items-center gap-2">
                        <Avatar className="w-6 h-6"><AvatarFallback className="text-[9px] bg-brand-navy text-white">{(v.agent || '').split(' ').map(n => n[0]).join('')}</AvatarFallback></Avatar>
                        <span className="text-slate-700 text-xs">{v.agent}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={statusColor[v.status] || 'bg-slate-50 text-slate-700 border-slate-200'}>
                        {v.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        {canEdit && (
                          <VisitFormDialog
                            leads={leads} properties={props}
                            initial={v}
                            onSaved={onSaved}
                            trigger={<Button size="icon" variant="ghost"><Icons.Pencil className="w-4 h-4" /></Button>}
                          />
                        )}
                        {canDelete && <DeleteButton onConfirm={() => onDelete(v)} />}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : !loading && (
        <Card className="border-slate-200">
          <CardContent className="p-4 sm:p-6">
            <div className="relative">
              <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-slate-200" />
              <div className="space-y-6">
                {filteredData.length === 0 && (
                  <div className="text-center text-slate-400 italic text-sm font-medium pt-2">
                    No timeline tracking objects recorded for these criteria.
                  </div>
                )}
                {filteredData.map(v => (
                  <div key={v.id} className="flex gap-4 relative">
                    <div className="w-4 h-4 mt-1 rounded-full bg-brand-gold border-4 border-white shadow z-10" />
                    <div className="flex-1 pb-2 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-semibold text-slate-900 truncate">
                          <span className="text-brand-gold mr-2 text-[10px] font-mono tracking-tighter">
                            {formatVisitId(v.id, v.date)}
                          </span>
                          {v.lead}
                        </div>
                        <Badge variant="outline" className={`text-[10px] h-5 ${statusColor[v.status] || 'bg-slate-50'}`}>
                          {v.status}
                        </Badge>
                      </div>
                      <div className="text-sm text-slate-600 truncate">{v.property}</div>
                      <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap gap-x-3">
                        <span><Icons.Calendar className="inline w-3 h-3 mr-1" />{v.date} — {v.time}</span>
                        <span><Icons.User className="inline w-3 h-3 mr-1" />{v.agent}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SiteVisits;